import { useTranslation } from 'react-i18next';
import { panelRuleToPhrase, type PanelRule, type WireColor } from '@il-colpo/engine';
import { Button, Icon } from '../../ui/index.ts';
import { useGame } from '../GameContext.tsx';
import { useLooseT } from '../text/loose.ts';
import { SWATCH } from './objects/colors.ts';
import { PanelObject } from './objects/PanelObject.tsx';
import { Bystander, ClueLine, HeardList, type PuzzleProps } from './shared.tsx';
import styles from './puzzles.module.css';

interface PanelView {
  wireCount: number;
  wires?: { color: WireColor; done: boolean }[];
  serial?: string;
  clues: { panelManual?: PanelRule[] };
}

const HEARD = ['panelRule', 'panelRuleLetter', 'panelSerial', 'panelWire'] as const;

/** Electrical panel (GDD §8.3): the hacker cuts or connects wires as the manual says. */
export function PanelPanel({ puzzleId, type, v: raw }: PuzzleProps) {
  const { t } = useTranslation('game');
  const { role, act } = useGame();
  const colour = useLooseT('phrases');
  const v = raw as unknown as PanelView;

  if (role === 'hacker' && v.wires) {
    return (
      <div className={styles['stack']}>
        <PanelObject wires={v.wires} />
        <p className={styles['serial']}>
          {t('puzzle.panel.serial')} <strong>{v.serial}</strong>
        </p>
        <ul className={styles['wires']}>
          {v.wires.map((w, i) => (
            <li key={i} className={[styles['wire'], w.done ? styles['done'] : ''].join(' ')}>
              <span className={styles['wireIndex']}>{i + 1}</span>
              <span
                className={styles['swatch']}
                style={{ background: SWATCH[w.color] }}
                aria-hidden="true"
              />
              <span className={styles['wireName']}>{colour(`enum.${w.color}`)}</span>
              {w.done ? (
                <Icon name="check" title={t('puzzle.panel.done')} />
              ) : (
                <span className={styles['wireActions']}>
                  <Button
                    size="md"
                    variant="secondary"
                    onClick={() => act(puzzleId, { kind: 'panel.act', wire: i, act: 'cut' })}
                  >
                    {t('puzzle.panel.cut')}
                  </Button>
                  <Button
                    size="md"
                    variant="secondary"
                    onClick={() => act(puzzleId, { kind: 'panel.act', wire: i, act: 'connect' })}
                  >
                    {t('puzzle.panel.connect')}
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
        <HeardList puzzleId={puzzleId} ids={HEARD} />
      </div>
    );
  }

  const manual = v.clues.panelManual;
  if (manual) {
    return (
      <div className={styles['stack']}>
        <p className={styles['muted']}>{t('puzzle.panel.manualHint')}</p>
        <ol className={styles['clues']}>
          {manual.map((rule, i) => (
            <ClueLine key={i} phrase={panelRuleToPhrase(i + 1, rule)} />
          ))}
        </ol>
      </div>
    );
  }
  return <Bystander type={type} />;
}
